import { useEffect, useMemo, useState } from 'react';
import { Button } from '@skupreme/shared/ui';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ControlledTextAreaFormField,
  DropdownFormField,
  FormInputSuffix,
  MaskedCurrencyFormField,
  MaskedNumberFormField,
} from '@elements/FormField';
import { ToastMessage } from '@utility/toast';
import { ErrorMessageConstant } from '@utility/constants/common';
import { LoaderBar } from '@components/LoaderBar';
import PurchaseOrdersService from '@utility/services/purchase-order';
import { CommonUtility } from '@utility/common';
import { AppModal } from '@elements/Modal';
import { DiscountRules } from './common';

const OperationList = [
  {
    text: 'Add',
    value: 'add',
  },
  {
    text: 'Subtract',
    value: 'subtract',
  },
];

const DiscountRulesList = [
  {
    text: 'Percentage',
    value: DiscountRules.Percentage,
  },
  {
    text: 'Fixed',
    value: 'amount',
  },
];

const adjustPriceSchema = yup.object().shape({
  operation: yup.string().required(),
  unit: yup.string().required(),
  value: yup.number().required().positive(),
  comment: yup.string(),
});
export function AdjustPricePopup({
  open,
  closeModal,
  item,
  currentTotal,
  currency,
}) {
  const [saving, setSaving] = useState('');

  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(adjustPriceSchema),
    shouldUnregister: true,
  });

  // @ts-expect-error - TS2769: No overload matches this call.
  const [unit, operation, value] = watch(['unit', 'operation', 'value']);

  const newTotal = useMemo(() => {
    if (value && unit && operation) {
      let tempValue = +value;
      if (unit === 'percentage') {
        tempValue = (currentTotal * +value) / 100;
      }
      if (operation === 'add') {
        return currentTotal + tempValue;
      }
      return currentTotal - tempValue;
    }
    return currentTotal;
  }, [currentTotal, unit, operation, value]);

  useEffect(() => {
    reset({});
  }, [open]);

  const onSubmit = async formData => {
    try {
      setSaving('Saving');
      const result = await PurchaseOrdersService.adjustment(item._id, formData);

      ToastMessage.success('The cost adjustment has been applied.');
      closeModal(result.data.adjustments);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving('');
    }
  };

  return (
    <AppModal open={open} title="Adjust Cost" closeModal={() => closeModal()}>
      {saving && <LoaderBar content={saving} />}
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="sk:flex sk:flex-col"
      >
        <div className="sk:grid sk:grid-cols-1 sk:gap-x-3 sk:sm:grid-cols-3">
          <DropdownFormField
            name="operation"
            options={OperationList}
            control={control}
            error={errors.operation}
            label="Operation"
            placeholder="Select"
            // @ts-expect-error - TS2322: Type '{ name: string; options: { text: string; value: string; }[]; control: Cont...
            search={false}
          />
          <DropdownFormField
            name="unit"
            options={DiscountRulesList}
            control={control}
            error={errors.unit}
            label="Adjust By"
            placeholder="Select"
            // @ts-expect-error - TS2322: Type '{ name: string; options: { text: string; value: string; }[]; control: Cont...
            search={false}
          />

          {unit === 'amount' ? (
            <MaskedCurrencyFormField
              name="value"
              error={errors.value}
              label="Amount"
              control={control}
              // @ts-expect-error - TS2322: Type 'string' is not assignable to type 'null | undefined'.
              defaultValue=""
              placeholder="0"
              maskOptions={{
                integerLimit: 3,
              }}
              currencyCode={currency}
            />
          ) : (
            <MaskedNumberFormField
              name="value"
              error={errors.value}
              label="Amount"
              control={control}
              defaultValue=""
              placeholder="0"
              maskOptions={{
                allowDecimal: true,
                decimalSymbol: '.',
                decimalLimit: 2,
                integerLimit: 3,
              }}
              maxValue={100}
              inputRight={<FormInputSuffix>%</FormInputSuffix>}
            />
          )}
        </div>
        <div className="sk:mb-4 sk:flex sk:flex-wrap sk:justify-between sk:gap-2">
          <div>
            <span className="sk:mr-2 sk:text-fg-tertiary">Old Cost</span>
            <span>{CommonUtility.currencyFormat(currentTotal, currency)}</span>
          </div>
          <div className="sk:text-right">
            <span className="sk:mr-2 sk:text-fg-tertiary">New Cost</span>
            <span>{CommonUtility.currencyFormat(newTotal, currency)}</span>
          </div>
        </div>
        <ControlledTextAreaFormField
          placeholder="Internal Note"
          name="comment"
          control={control}
          width={16}
        />
        <div className="sk:flex sk:justify-end sk:gap-2">
          <Button onPress={() => closeModal()}>Cancel</Button>
          <Button type="submit" variant="primary">
            Adjust Cost
          </Button>
        </div>
      </form>
    </AppModal>
  );
}
